"use client";

import { useEffect, useLayoutEffect, useSyncExternalStore } from "react";
import { useUserInfo, type UserInfoResolutionFailure } from "~/components/user-info-provider";
import { UserRoleEnum } from "~/lib/objects";
import { authClient } from "~/utils/auth-client";

type AccessStatus = "admin" | "checking" | "error" | "anonymous" | "non-admin";
type Observation = {
  status: AccessStatus;
  identity: object;
  failure?: UserInfoResolutionFailure;
  sdkError?: { status: number } | null;
  sdkPending: boolean;
  sessionKey?: string | null;
};
type Completion = { lifetime: object; current: () => boolean; apply: () => void };

class AdminAccess {
  private observation: Observation;
  private snapshot: { status: AccessStatus; lifetime: object; denialEpisode: object | null };
  private denialEpisode: object | null = null;
  private listeners = new Set<() => void>();
  private completions: Completion[] = [];
  private lastFailure?: object;
  private reconciling = false;
  private observedPending = false;
  private blocked = false;
  private lastSdkError?: object;
  private sdkRunning = false;
  private cycle = {};
  private sessionKey?: string | null;

  constructor(observation: Observation, private refetchUserInfo: () => void, private refetchSession: () => Promise<unknown>, private invalidateUserInfo: () => void) {
    this.observation = observation;
    this.sessionKey = observation.sessionKey;
    this.snapshot = { status: observation.status, lifetime: {}, denialEpisode: null };
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };
  getSnapshot = () => this.snapshot;
  retry = () => {
    if (this.snapshot.status !== "error") return;
    this.blocked = false;
    this.reconciling = true;
    this.observedPending = false;
    this.snapshot = { ...this.snapshot, status: "checking" };
    for (const listener of this.listeners) listener();
    if (this.observation.sdkError) this.recheckSession();
    else this.refetchUserInfo();
  };
  isCurrent = (lifetime: object) => this.snapshot.lifetime === lifetime &&
    this.snapshot.status !== "anonymous" && this.snapshot.status !== "non-admin";
  reportDenial = (lifetime: object, status: number) => {
    if (status !== 401 && status !== 403) return false;
    if (!this.isCurrent(lifetime)) return true;
    this.denialEpisode = {};
    this.completions = [];
    this.reconciling = true;
    this.observedPending = false;
    this.snapshot = { status: "checking", lifetime: {}, denialEpisode: this.denialEpisode };
    for (const listener of this.listeners) listener();
    this.invalidateUserInfo();
    if (status === 401 || this.observation.sdkError || this.observation.sdkPending) this.recheckSession();
    else this.refetchUserInfo();
    return true;
  };

  observe(observation: Observation) {
    const replaced = observation.sessionKey !== undefined && this.sessionKey !== undefined && observation.sessionKey !== this.sessionKey;
    if (observation.sessionKey !== undefined) this.sessionKey = observation.sessionKey;
    if (replaced) {
      this.denialEpisode = null;
      this.cycle = {};
      this.reconciling = false;
      this.blocked = false;
      this.sdkRunning = false;
      this.observedPending = false;
      this.lastFailure = undefined;
      this.lastSdkError = undefined;
    }
    let departed = replaced || observation.identity !== this.observation.identity ||
      (observation.status === "anonymous" || observation.status === "non-admin") && observation.status !== this.observation.status;
    const sdkDenied = observation.sdkError?.status === 401 || observation.sdkError?.status === 403;
    const newSdkDenial = sdkDenied && observation.sdkError !== this.lastSdkError;
    const newUserDenial = observation.failure?.kind === "denied" && observation.failure.episode !== this.lastFailure;
    this.observation = observation;
    if (newSdkDenial || newUserDenial) {
      if (this.denialEpisode === null || !this.reconciling && !this.blocked) this.denialEpisode = {};
      if (newSdkDenial) this.lastSdkError = observation.sdkError ?? undefined;
      if (newUserDenial) this.lastFailure = observation.failure?.episode;
      departed = true;
      if (this.reconciling || this.blocked) {
        this.blocked = true;
        this.sdkRunning = false;
        this.cycle = {};
      }
      else {
        this.reconciling = true;
        this.observedPending = false;
        // The provider has already invalidated this denial. Consume it once.
        if (newSdkDenial || observation.failure?.status === 401) this.recheckSession();
        else this.refetchUserInfo();
      }
    }
    // A verified negative permission settles admission even if another consumer
    // performed the refresh. It never grants privileged use from cached data.
    if (observation.status === "non-admin" || observation.status === "anonymous") {
      this.blocked = false;
      this.reconciling = false;
      this.sdkRunning = false;
      this.observedPending = false;
      this.cycle = {};
    }
    if (this.reconciling && observation.status === "checking") this.observedPending = true;
    if (this.reconciling && !this.sdkRunning && this.observedPending && observation.status !== "checking") {
      if (observation.status === "error") this.blocked = true;
      else this.reconciling = false;
    }
    this.observation = observation;
    if (departed) this.completions = [];
    const status = this.blocked ? "error" : this.reconciling ? "checking" : observation.status;
    if (departed || this.snapshot.status !== status || this.snapshot.denialEpisode !== this.denialEpisode) {
      this.snapshot = { status, lifetime: departed ? {} : this.snapshot.lifetime, denialEpisode: this.denialEpisode };
      for (const listener of this.listeners) listener();
    }
  }

  // Consumers must first render the confirmed snapshot so their follow-up
  // request guards observe the same authorization as the deferred outcome.
  flushCompletions = () => {
    if (this.snapshot.status !== "admin") return;
    const completions = this.completions;
    this.completions = [];
    for (const completion of completions) {
      this.settle(completion.lifetime, completion.current, completion.apply);
    }
  };

  private recheckSession() {
    this.sdkRunning = true;
    const cycle = this.cycle = {};
    void this.refetchSession().then(() => {
      if (this.cycle !== cycle) return;
      this.sdkRunning = false;
      const observation = this.observation;
      if (observation.sdkPending) return;
      if (observation.sdkError) {
        this.blocked = true;
        this.observe(observation);
      } else if (observation.status === "error" || observation.status === "admin" && !this.observedPending) {
        this.refetchUserInfo();
      } else this.observe(observation);
    }, () => {
      if (this.cycle !== cycle) return;
      this.sdkRunning = false;
      this.blocked = true;
      this.observe(this.observation);
    });
  }

  settle = (lifetime: object, current: () => boolean, apply: () => void) => {
    if (!this.isCurrent(lifetime) || !current()) return;
    if (this.snapshot.status === "admin") apply();
    else this.completions.push({ lifetime, current, apply });
  };
}

// Scope coordination to this provider without changing student-capable identity.
const coordinators = new WeakMap<() => void, AdminAccess>();

export function useAdminAccess() {
  const user = useUserInfo();
  const session = authClient.useSession();
  const pending = session.isPending || ("isRefetching" in session && session.isRefetching === true);
  const status: AccessStatus = pending || user.resolutionStatus === "pending" ? "checking"
    : session.error || user.resolutionStatus === "error" ? "error"
    : user.resolutionStatus === "anonymous" ? "anonymous"
    : !user.userInfo ? "checking"
    : user.userInfo?.role === UserRoleEnum.ADMIN ? "admin" : "non-admin";
  const sessionKey = pending || session.error ? undefined : session.data ? JSON.stringify([session.data.session.id, session.data.user.id]) : null;
  let coordinator = coordinators.get(user.refetchUserInfo);
  const observation: Observation = { status, identity: user.resolutionLifetime, failure: user.resolutionFailure, sdkError: session.error, sdkPending: pending, sessionKey };
  if (!coordinator) {
    coordinator = new AdminAccess(observation, user.refetchUserInfo, async () => session.refetch(), user.invalidateUserInfo);
    coordinators.set(user.refetchUserInfo, coordinator);
  }
  const access = coordinator;
  const snapshot = useSyncExternalStore(access.subscribe, access.getSnapshot, access.getSnapshot);
  useLayoutEffect(() => {
    access.observe({ status, identity: user.resolutionLifetime, failure: user.resolutionFailure, sdkError: session.error, sdkPending: pending, sessionKey });
  }, [access, status, user.resolutionLifetime, user.resolutionFailure, session.error, pending, sessionKey]);
  useEffect(() => { access.flushCompletions(); }, [access, snapshot]);
  return {
    ...snapshot,
    canRequest: status === "admin" && snapshot.status === "admin",
    isCurrent: access.isCurrent,
    settle: access.settle,
    retry: access.retry,
    reportDenial: access.reportDenial,
  };
}
